import type { UnitPriceBasis } from "./types";
import type { RequiredCard } from "./price-conditions";

export const SHOPPING_LIST_STORAGE_KEY = "kiloprice.shopping-list.v1";
export const EXPIRED_OFFER_RETENTION_DAYS = 30;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MILLISECONDS = 86_400_000;

export interface ShoppingListUnitPrice {
  basis: UnitPriceBasis;
  minimumMdl: number;
  maximumMdl: number;
  isRange: boolean;
}

export interface ShoppingListItem {
  requiredCard?: RequiredCard;
  key: string;
  quantity: number;
  title: string;
  originalTitle: string | null;
  /** Added in-place to v1; older saved/shared rows legitimately omit it. */
  titleUk?: string | null;
  store: string;
  catalogTitle: string;
  image: string;
  newPriceMdl: number | null;
  oldPriceMdl: number | null;
  weightOrVolume: string | null;
  unitPrice: ShoppingListUnitPrice | null;
  validUntil: string;
}

export interface ShoppingCatalogGroup {
  title: string;
  validUntil: string;
  expired: boolean;
  firstIndex: number;
  items: ShoppingListItem[];
}

export interface ShoppingStoreGroup {
  store: string;
  firstIndex: number;
  catalogs: ShoppingCatalogGroup[];
}

export interface ShoppingMoneyTotal {
  totalBani: number;
  knownPriceRows: number;
  unknownPriceRows: number;
  complete: boolean;
}

export interface ShoppingStoreMoneyTotal extends ShoppingMoneyTotal {
  store: string;
}

export interface ShoppingMoneySummary {
  stores: ShoppingStoreMoneyTotal[];
  overall: ShoppingMoneyTotal;
}

interface ShoppingListPayload {
  version: 1;
  items: ShoppingListItem[];
}

const MAX_QUANTITY = 99;

function validDateKey(value: string): boolean {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return (
    Number.isFinite(timestamp) &&
    new Date(timestamp).toISOString().slice(0, 10) === value
  );
}

function dateKeyDaysBefore(value: string, days: number): string | null {
  if (!validDateKey(value)) {
    return null;
  }
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return new Date(timestamp - days * DAY_MILLISECONDS)
    .toISOString()
    .slice(0, 10);
}

export function shoppingOfferExpired(
  item: ShoppingListItem,
  today: string,
): boolean {
  return (
    validDateKey(today) &&
    validDateKey(item.validUntil) &&
    item.validUntil < today
  );
}

/** Export is fail-closed on malformed dates; does not delete saved history. */
export function activeShoppingShareItems(items: readonly ShoppingListItem[], today: string): ShoppingListItem[] {
  if (!validDateKey(today)) return [];
  return items.filter((item) => validDateKey(item.validUntil) && item.validUntil >= today);
}

export function shoppingItemsByLifecycle(
  items: readonly ShoppingListItem[],
  today: string,
): { active: ShoppingListItem[]; expired: ShoppingListItem[] } {
  const active: ShoppingListItem[] = [];
  const expired: Array<{ item: ShoppingListItem; index: number }> = [];
  items.forEach((item, index) => {
    if (shoppingOfferExpired(item, today)) {
      expired.push({ item, index });
    } else {
      active.push(item);
    }
  });
  expired.sort(
    (left, right) =>
      right.item.validUntil.localeCompare(left.item.validUntil) ||
      left.index - right.index,
  );
  return { active, expired: expired.map(({ item }) => item) };
}

function compareCatalogUrgency(
  left: ShoppingCatalogGroup,
  right: ShoppingCatalogGroup,
): number {
  if (left.expired !== right.expired) {
    return left.expired ? 1 : -1;
  }
  const dateOrder = left.expired
    ? right.validUntil.localeCompare(left.validUntil)
    : left.validUntil.localeCompare(right.validUntil);
  return dateOrder || left.firstIndex - right.firstIndex;
}

export function shoppingItemsByStoreAndCatalog(
  items: readonly ShoppingListItem[],
  currentDateKey: string,
): ShoppingStoreGroup[] {
  const stores = new Map<
    string,
    {
      firstIndex: number;
      catalogs: Map<string, ShoppingCatalogGroup>;
    }
  >();
  items.forEach((item, index) => {
    let store = stores.get(item.store);
    if (!store) {
      store = { firstIndex: index, catalogs: new Map() };
      stores.set(item.store, store);
    }
    const catalogKey = `${item.validUntil}\u0000${item.catalogTitle}`;
    const catalog = store.catalogs.get(catalogKey);
    if (catalog) {
      catalog.items.push(item);
      return;
    }
    store.catalogs.set(catalogKey, {
      title: item.catalogTitle.trim(),
      validUntil: item.validUntil,
      expired: shoppingOfferExpired(item, currentDateKey),
      firstIndex: index,
      items: [item],
    });
  });
  const groups = [...stores.entries()].map(([store, value]) => ({
    store,
    firstIndex: value.firstIndex,
    catalogs: [...value.catalogs.values()].sort(compareCatalogUrgency),
  }));
  return groups.sort((left, right) => {
    const urgency = compareCatalogUrgency(
      left.catalogs[0] as ShoppingCatalogGroup,
      right.catalogs[0] as ShoppingCatalogGroup,
    );
    return urgency || left.firstIndex - right.firstIndex;
  });
}

function escapeRegularExpression(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function shoppingCatalogDisplayTitle(
  title: string,
  store: string,
): string {
  const normalized = title.trim();
  if (!normalized) {
    return "";
  }
  const storePattern = escapeRegularExpression(store);
  const generic = new RegExp(`^catalog\\s+${storePattern}$`, "i");
  if (generic.test(normalized)) {
    return "";
  }
  const named = new RegExp(`^catalog\\s+(.+?)\\s+${storePattern}$`, "i").exec(
    normalized,
  );
  return named?.[1]?.trim() || normalized;
}

export function pruneExpiredShoppingItems(
  items: readonly ShoppingListItem[],
  today: string,
  retentionDays = EXPIRED_OFFER_RETENTION_DAYS,
): ShoppingListItem[] {
  const safeRetentionDays = Number.isFinite(retentionDays)
    ? Math.max(0, Math.trunc(retentionDays))
    : EXPIRED_OFFER_RETENTION_DAYS;
  const cutoff = dateKeyDaysBefore(today, safeRetentionDays);
  if (!cutoff) {
    return [...items];
  }
  return items.filter(
    (item) => !validDateKey(item.validUntil) || item.validUntil >= cutoff,
  );
}

function optionalString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function optionalNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function optionalUnitPrice(value: unknown): ShoppingListUnitPrice | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Partial<ShoppingListUnitPrice>;
  if (
    (candidate.basis !== "kg" &&
      candidate.basis !== "l" &&
      candidate.basis !== "10pcs" &&
      candidate.basis !== "100pcs" &&
      candidate.basis !== "100sheets") ||
    typeof candidate.minimumMdl !== "number" ||
    !Number.isFinite(candidate.minimumMdl) ||
    typeof candidate.maximumMdl !== "number" ||
    !Number.isFinite(candidate.maximumMdl) ||
    typeof candidate.isRange !== "boolean"
  ) {
    return null;
  }
  return {
    basis: candidate.basis,
    minimumMdl: candidate.minimumMdl,
    maximumMdl: candidate.maximumMdl,
    isRange: candidate.isRange,
  };
}

function normalizeItem(value: unknown): ShoppingListItem | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Partial<ShoppingListItem>;
  if (
    typeof candidate.key !== "string" ||
    !candidate.key ||
    typeof candidate.title !== "string" ||
    !candidate.title ||
    typeof candidate.store !== "string" ||
    !candidate.store ||
    typeof candidate.catalogTitle !== "string" ||
    typeof candidate.image !== "string" ||
    typeof candidate.validUntil !== "string" ||
    (candidate.requiredCard !== undefined && candidate.requiredCard !== "casa_mega_digital")
  ) {
    return null;
  }
  const rawQuantity = Number(candidate.quantity);
  const quantity = Number.isFinite(rawQuantity)
    ? Math.min(MAX_QUANTITY, Math.max(1, Math.trunc(rawQuantity)))
    : 1;
  const titleUk = optionalString(candidate.titleUk);
  return {
    key: candidate.key,
    quantity,
    title: candidate.title,
    originalTitle: optionalString(candidate.originalTitle),
    ...(titleUk ? { titleUk } : {}),
    ...(candidate.requiredCard === "casa_mega_digital" ? { requiredCard: candidate.requiredCard } : {}),
    store: candidate.store,
    catalogTitle: candidate.catalogTitle,
    image: candidate.image,
    newPriceMdl: optionalNumber(candidate.newPriceMdl),
    oldPriceMdl: optionalNumber(candidate.oldPriceMdl),
    weightOrVolume: optionalString(candidate.weightOrVolume),
    unitPrice: optionalUnitPrice(candidate.unitPrice),
    validUntil: candidate.validUntil,
  };
}

export function parseShoppingList(raw: string | null): ShoppingListItem[] {
  if (!raw) {
    return [];
  }
  try {
    const payload = JSON.parse(raw) as Partial<ShoppingListPayload>;
    if (payload.version !== 1 || !Array.isArray(payload.items)) {
      return [];
    }
    const unique = new Map<string, ShoppingListItem>();
    for (const value of payload.items) {
      const item = normalizeItem(value);
      if (item) {
        unique.set(item.key, item);
      }
    }
    return [...unique.values()];
  } catch {
    return [];
  }
}

export function serializeShoppingList(items: ShoppingListItem[]): string {
  const payload: ShoppingListPayload = {
    version: 1,
    items: items.flatMap((item) => {
      const normalized = normalizeItem(item);
      return normalized ? [normalized] : [];
    }),
  };
  return JSON.stringify(payload);
}

export function setShoppingQuantity(
  items: ShoppingListItem[],
  key: string,
  quantity: number,
): ShoppingListItem[] {
  const nextQuantity = Math.min(
    MAX_QUANTITY,
    Math.max(1, Math.trunc(Number.isFinite(quantity) ? quantity : 1)),
  );
  return items.map((item) =>
    item.key === key ? { ...item, quantity: nextQuantity } : item,
  );
}

/** Shared by add controls: zero removes the row; repeated taps keep one snapshot. */
export function adjustShoppingQuantity(
  items: ShoppingListItem[],
  key: string,
  delta: -1 | 1,
  newItem?: ShoppingListItem,
): ShoppingListItem[] {
  const current = items.find((item) => item.key === key);
  if (!current) {
    return delta === 1 && newItem?.key === key
      ? [...items, { ...newItem, quantity: 1 }]
      : items;
  }
  const quantity = Math.min(MAX_QUANTITY, current.quantity + delta);
  if (quantity <= 0) return items.filter((item) => item.key !== key);
  if (quantity === current.quantity) return items;
  return setShoppingQuantity(items, key, quantity);
}

/** Explicit cleanup, not the 30-day automatic retention policy. Unknown dates stay. */
export function removeExpiredShoppingItems(
  items: ShoppingListItem[],
  today: string,
): ShoppingListItem[] {
  return items.filter((item) => !shoppingOfferExpired(item, today));
}

export function mergeShoppingLists(
  current: ShoppingListItem[],
  incoming: ShoppingListItem[],
): ShoppingListItem[] {
  const merged = new Map(
    current.map((item) => [item.key, { ...item }] as const),
  );
  for (const item of incoming) {
    const existing = merged.get(item.key);
    merged.set(
      item.key,
      existing
        ? {
            ...item,
            quantity: Math.min(MAX_QUANTITY, existing.quantity + item.quantity),
          }
        : { ...item },
    );
  }
  return [...merged.values()];
}

export function shoppingQuantityTotal(items: ShoppingListItem[]): number {
  return items.reduce((total, item) => total + item.quantity, 0);
}

/**
 * Converts one offer price to integer bani before quantities are applied.
 * Decimal shifting avoids the familiar `1.005 * 100 === 100.499…` trap.
 */
export function shoppingOfferPriceBani(value: number | null): number | null {
  if (value === null || !Number.isFinite(value) || value < 0) {
    return null;
  }
  const [coefficient, rawExponent = "0"] = String(value).toLowerCase().split("e");
  const exponent = Number(rawExponent);
  const shifted = Number(`${coefficient}e${exponent + 2}`);
  if (!Number.isFinite(shifted)) {
    return null;
  }
  const bani = Math.round(shifted);
  return Number.isSafeInteger(bani) ? bani : null;
}

/** One authoritative receipt calculation for the Mini App, website and bot. */
export function calculateShoppingMoneySummary(
  items: readonly ShoppingListItem[],
  today: string,
): ShoppingMoneySummary {
  const stores = new Map<string, ShoppingStoreMoneyTotal>();
  let totalBani = 0;
  let knownPriceRows = 0;
  let unknownPriceRows = 0;

  for (const item of items) {
    if (shoppingOfferExpired(item, today)) {
      continue;
    }
    let store = stores.get(item.store);
    if (!store) {
      store = {
        store: item.store,
        totalBani: 0,
        knownPriceRows: 0,
        unknownPriceRows: 0,
        complete: true,
      };
      stores.set(item.store, store);
    }
    const unitBani = shoppingOfferPriceBani(item.newPriceMdl);
    const rowBani = unitBani === null ? null : unitBani * item.quantity;
    if (rowBani === null || !Number.isSafeInteger(rowBani)) {
      store.unknownPriceRows += 1;
      unknownPriceRows += 1;
      continue;
    }
    store.totalBani += rowBani;
    store.knownPriceRows += 1;
    totalBani += rowBani;
    knownPriceRows += 1;
  }

  const storeTotals = [...stores.values()].map((store) => ({
    ...store,
    complete: store.unknownPriceRows === 0,
  }));
  return {
    stores: storeTotals,
    overall: {
      totalBani,
      knownPriceRows,
      unknownPriceRows,
      complete: unknownPriceRows === 0,
    },
  };
}
